import { translationService } from "../../services/translation/TranslationService";
import { AnalysisDetection } from "./analysisDetections";
import { ObservationWording, wordingFor } from "./observationVocabulary";

/** mm:ss, the way the graph and the detection cards state a video position. */
const clock = (seconds: number): string => {
  const total = Math.max(0, Math.round(seconds));
  return `${Math.floor(total / 60)}:${(total % 60).toString().padStart(2, "0")}`;
};

const trimmed = (value?: string): string => (value ?? "").trim();

/**
 * The coded fields an observation text can be written from — whatever of them is filled in.
 *
 * Deliberately not tied to one caller's shape: the Inspection Graph hands over an
 * {@link AnalysisDetection}, the edit dialog hands over what the reviewer has typed into the
 * boxes, and both have to produce the same sentence from the same values.
 */
export interface ObservationFields {
  mainCode?: string;
  c1?: string;
  c2?: string;
  q1?: string;
  q2?: string;
  sa?: string;
  positionFrom?: string;
  positionTo?: string;
  /** Station in metres, already formatted — a single station, or a region's span. */
  position?: string;
  distance?: number | string;
  timeSeconds?: number;
  /** The model's confidence, 0..1. Absent on a row the reviewer created themselves. */
  confidence?: number;
}

/**
 * One quantity as the standards wording states it: "Querschnittsreduzierung = 2%".
 *
 * A value that already carries its own label is passed through — the OCR pipeline stores the
 * whole phrase in q1 ("Breite = 2mm") while {@link parseVideoText} stores the bare number, and
 * both have to render as the same sentence.
 */
function quantity(value: string, label: string, unit: string): string {
  const trimmedValue = value.trim();
  if (trimmedValue.includes("=")) return trimmedValue;
  // No label to give: the wording already names the measurement ("..., Querschnittsreduzierung
  // %q1%"), so the slot is the value alone. Printing "= 2%" there would name it twice.
  if (label.trim() === "") return `${trimmedValue}${unit}`;
  return `${label} = ${trimmedValue}${unit}`;
}

/**
 * The coded observation in the wording an inspection van prints, e.g.
 * "Pos: 9 - 3; Wurzeln, einzelne, feine Wurzeln, Querschnittsreduzierung = 2%, an Verbindung".
 *
 * Assembled from the vocabulary entry for the code, not written per caller: the entry carries
 * the sentence with %q1%/%q2% where the quantities belong, because a quantity sits BEFORE the
 * trailing qualifier and appending it would put "an Verbindung" in the middle of a measurement.
 *
 * A quantity nobody entered takes its separator with it, so a video-analysis row — which
 * reports a code and nothing else — renders as the description alone rather than as a sentence
 * with a gap in it.
 */
function standardsText(wording: ObservationWording, fields: ObservationFields): string {
  const values = [trimmed(fields.q1), trimmed(fields.q2)];

  let body = wording.text;
  wording.quantities.forEach((q, index) => {
    const placeholder = `%q${index + 1}%`;
    const value = values[index];
    body = value
      ? body.replace(placeholder, quantity(value, q.label, q.unit))
      // Take the separator in front of the placeholder with it; ", %q1%, an Verbindung"
      // has to close up to ", an Verbindung", not to ", , an Verbindung".
      : body.replace(new RegExp(`\\s*[,.;]?\\s*${placeholder}`), "");
  });
  // A placeholder the table declares but this caller knows nothing about.
  body = body.replace(/\s*[,.;]?\s*%q\d%/g, "").trim();

  // A quantity the reviewer entered against wording that declares no slot for it -- which is
  // every main-code entry, because which quantity a code states depends on the characterisation
  // and that is exactly what is unknown there. Appended by its column name rather than a guessed
  // label: "Q1 = 30" states the value the reviewer entered without claiming what it measures.
  if (wording.quantities.length === 0) {
    // The value as the van prints it -- "2%", not "Q1 = 2%". The column header already says
    // which quantity it is, and a label nobody chose is worse than none.
    const stated = [values[0], values[1]].filter(Boolean);
    if (stated.length > 0) body = `${body}, ${stated.join(", ")}`;
  }

  const from = trimmed(fields.positionFrom);
  const to = trimmed(fields.positionTo);
  const position = from && to ? `Pos: ${from} - ${to}` : from || to ? `Pos: ${from || to}` : "";

  return position ? `${position}; ${body}` : body;
}

/**
 * An observation text written from the coded fields.
 *
 * Video analysis reports codes, not prose. There is no ocr.json and no inspector's note behind a
 * VA run, so a damage approved straight off the Inspection Graph would reach the ISYBAU/DWA
 * observation text — and the reviewed JSON, CSV and HTML — with an empty description. This states
 * what the fields actually say: the code with its characterisations, the quantification and
 * clock position when they are filled in, where along the pipe, when in the video, and how sure
 * the model was.
 *
 * The counterpart of {@link parseVideoText}, which goes the other way: that reads the van's
 * burned-in overlay text into the boxes, this writes the boxes back out as a sentence.
 *
 * Only ever a STARTING point — no caller overwrites a text the reviewer has already written.
 *
 * Values are substituted into the translated template directly rather than through translate()'s
 * context parameter, because that runs string values back through the dictionary and would try to
 * look up "12.40" as a translation key.
 */
export function observationTextFromFields(fields: ObservationFields): string {
  // The standards wording for this exact code combination, when the corpus pins one down.
  // Everything else falls through to the plain sentence below: an unknown code has no
  // authored wording anywhere, and inventing one would state a characterisation the model
  // never reported.
  const wording = wordingFor(fields.mainCode, fields.c1, fields.c2);
  if (wording) return standardsText(wording, fields);

  const code = `${trimmed(fields.mainCode)}${trimmed(fields.c1)}${trimmed(fields.c2)}`.trim();

  const station = fields.position ?? formatStation(fields.distance);
  const time = fields.timeSeconds === undefined ? "" : clock(fields.timeSeconds);

  // Quantification, standardised annotation and clock position: stated only when filled in, so
  // the sentence never claims a measurement nobody entered.
  const detail = [
    trimmed(fields.q1) && `Q1 ${trimmed(fields.q1)}`,
    trimmed(fields.q2) && `Q2 ${trimmed(fields.q2)}`,
    trimmed(fields.sa) && `SA ${trimmed(fields.sa)}`,
    positionSpan(fields),
  ]
    .filter(Boolean)
    .join(", ");

  const confidence =
    fields.confidence !== undefined && fields.confidence > 0
      ? translationService
          .translate("comparison.va-detections.generated-note.confidence")
          .replace("%value%", `${Math.round(fields.confidence * 100)}%`)
      : "";

  return translationService
    .translate("comparison.va-detections.generated-note")
    .replace("%code%", code || "—")
    .replace("%position%", station || time || "—")
    .replace("%time%", time || "—")
    .replace("%confidence%", confidence)
    .replace("%detail%", detail ? `, ${detail}` : "");
}

/** The clock position, however much of it is known. */
function positionSpan(fields: ObservationFields): string {
  const from = trimmed(fields.positionFrom);
  const to = trimmed(fields.positionTo);
  if (!from && !to) return "";
  if (from && to) return `Pos ${from}–${to}`;
  return `Pos ${from || to}`;
}

function formatStation(distance?: number | string): string {
  if (distance === undefined || distance === null || `${distance}`.trim() === "") return "";
  const value = typeof distance === "number" ? distance : Number(distance);
  return Number.isFinite(value) ? `${value.toFixed(2)} m` : "";
}

/**
 * The text for one row of the Inspection Graph — what a damage approved from there starts with.
 *
 * A region detection states its span rather than a single station: that is what the analysis
 * found, and flattening it to the peak metre would lose the extent the standards documents carry.
 */
export function analysisObservationText(d: AnalysisDetection): string {
  const hasSpan =
    d.beginDistance !== undefined &&
    d.endDistance !== undefined &&
    d.endDistance > d.beginDistance;

  return observationTextFromFields({
    mainCode: d.mainCode,
    c1: d.c1,
    c2: d.c2,
    position: hasSpan
      ? `${d.beginDistance!.toFixed(2)}–${d.endDistance!.toFixed(2)} m`
      : formatStation(d.meters > 0 ? d.meters : undefined),
    timeSeconds: d.timestamp,
    confidence: d.accuracy,
  });
}
